import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button,
  useToast, SimpleGrid, InputGroup, InputRightElement, Divider, Heading
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import SelectorPerfilUsuario from 'components/PerfilUsuario/SelectorPerfilUsuario';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onSubmit?: (data: any) => void;
}

const tiposSolicitud = ['Fraccionamiento', 'Lotificación'];
const tiposDesarrollo = ['Habitacional', 'Comercial', 'Industrial'];

const SolicitudFraccionamiento: React.FC<Props> = ({ modo, data, onSubmit }) => {
  const toast = useToast();

  const [tipoSolicitud, setTipoSolicitud] = useState(data?.tipoSolicitud || '');
  const [tipoDesarrollo, setTipoDesarrollo] = useState(data?.tipoDesarrollo || '');
  const [usoSuelo, setUsoSuelo] = useState(data?.usoSuelo || '');
  const [superficie, setSuperficie] = useState(data?.superficie || '');
  const [numLotes, setNumLotes] = useState(data?.numLotes || '');
  const [proyecto, setProyecto] = useState(data?.proyecto || '');
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [direccion, setDireccion] = useState(data?.direccion || '');
  const [lat, setLat] = useState(Number(data?.lat) || 19.4326);
  const [lng, setLng] = useState(Number(data?.lng) || -99.1332);
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [costo, setCosto] = useState(data?.costo || 0);

  const [perfilSeleccionado, setPerfilSeleccionado] = useState<any>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  useEffect(() => {
    const base = 90;
    setCosto(superficie * base);
  }, [superficie]);

  const handlePerfil = (perfil: any) => {
    setPerfilSeleccionado(perfil);
  };

  const buscarDireccion = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: direccion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setLat(loc.lat());
        setLng(loc.lng());
        setDireccion(results[0].formatted_address);
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Revisa la dirección ingresada.',
          status: 'error',
        });
      }
    });
  };

  const handleGuardar = async () => {
    const payload = {
      tipoSolicitud,
      tipoDesarrollo,
      usoSuelo,
      superficie,
      numLotes,
      proyecto,
      responsable,
      direccion,
      lat,
      lng,
      observaciones,
      costo,
      fechaRegistro: new Date(),
      usuarioId: perfilSeleccionado?.id || null,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/obras/solicitud-fraccionamiento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Error al guardar');

      toast({
        title: 'Guardado',
        description: 'La solicitud fue registrada.',
        status: 'success',
      });

      onSubmit?.(payload);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al registrar.',
        status: 'error',
      });
    }
  };

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <VStack spacing={6} align="stretch">
      {(modo === 'edit' || modo === 'new') ? (
        <>
          <Heading size="md">Datos del Solicitante</Heading>
          <SelectorPerfilUsuario onPerfilSeleccionado={handlePerfil} />

          <Divider />

          <Heading size="md">Datos del Proyecto</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            <Select placeholder="Tipo de solicitud" value={tipoSolicitud} onChange={(e) => setTipoSolicitud(e.target.value)}>
              {tiposSolicitud.map((op) => <option key={op} value={op}>{op}</option>)}
            </Select>
            <Select placeholder="Tipo de desarrollo" value={tipoDesarrollo} onChange={(e) => setTipoDesarrollo(e.target.value)}>
              {tiposDesarrollo.map((op) => <option key={op} value={op}>{op}</option>)}
            </Select>
            <Input placeholder="Uso de suelo" value={usoSuelo} onChange={(e) => setUsoSuelo(e.target.value)} />
            <Input type="number" placeholder="Superficie (m²)" value={superficie} onChange={(e) => setSuperficie(parseFloat(e.target.value))} />
            <Input type="number" placeholder="No. de lotes" value={numLotes} onChange={(e) => setNumLotes(parseInt(e.target.value))} />
            <Input placeholder="Nombre del proyecto" value={proyecto} onChange={(e) => setProyecto(e.target.value)} />
            <Input placeholder="Responsable técnico" value={responsable} onChange={(e) => setResponsable(e.target.value)} />
          </SimpleGrid>

          <Text fontWeight="semibold" color="green.600">
            Costo estimado: ${costo.toFixed(2)}
          </Text>

          <Divider />

          <Heading size="md">Ubicación del Proyecto</Heading>
          <InputGroup>
            <Input placeholder="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={buscarDireccion}>Buscar</Button>
            </InputRightElement>
          </InputGroup>

          <Box h="250px" w="100%" borderRadius="md" overflow="hidden" border="1px solid #ccc">
            <GoogleMap zoom={15} center={{ lat, lng }} mapContainerStyle={{ width: '100%', height: '100%' }}>
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={(e) => {
                  setLat(e.latLng?.lat() || lat);
                  setLng(e.latLng?.lng() || lng);
                }}
              />
            </GoogleMap>
          </Box>

          <Textarea placeholder="Observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />

          <Button colorScheme="green" onClick={handleGuardar}>
            {modo === 'new' ? 'Registrar Solicitud' : 'Actualizar Solicitud'}
          </Button>
        </>
      ) : (
        <>
          <Heading size="lg" mb={2}>Resumen de Solicitud</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
              <Text fontWeight="bold" mb={2}>Datos del Proyecto</Text>
              <Text><strong>Tipo Solicitud:</strong> {tipoSolicitud}</Text>
              <Text><strong>Tipo Desarrollo:</strong> {tipoDesarrollo}</Text>
              <Text><strong>Uso de Suelo:</strong> {usoSuelo}</Text>
              <Text><strong>Superficie:</strong> {superficie} m²</Text>
              <Text><strong>No. de Lotes:</strong> {numLotes}</Text>
              <Text><strong>Proyecto:</strong> {proyecto}</Text>
              <Text><strong>Responsable:</strong> {responsable}</Text>
              <Text><strong>Dirección:</strong> {direccion}</Text>
              <Text><strong>Costo:</strong> ${costo.toFixed(2)}</Text>
              <Text><strong>Fecha:</strong> {data?.fechaRegistro ? new Date(data.fechaRegistro).toLocaleString() : 'No disponible'}</Text>
            </Box>

            {observaciones && (
              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <Text fontWeight="bold" mb={2}>Observaciones</Text>
                <Text>{observaciones}</Text>
              </Box>
            )}
          </SimpleGrid>

          <Box h="250px" w="100%" borderRadius="md" overflow="hidden" border="1px solid #ccc">
            <GoogleMap zoom={15} center={{ lat, lng }} mapContainerStyle={{ width: '100%', height: '100%' }}>
              <Marker position={{ lat, lng }} />
            </GoogleMap>
          </Box>
        </>
      )}
    </VStack>
  );
};

export default SolicitudFraccionamiento;
